import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { FeatureAccessAPI } from '../services/api';
import {
  FiCheck, FiArrowLeft, FiUpload, FiPlus,
  FiLoader, FiImage, FiBriefcase, FiUsers,
  FiMapPin, FiEdit2, FiX,
  FiClock, FiGrid
} from 'react-icons/fi';
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLocation as useLocationHook } from './Tasks/hooks/useLocation';
import './Onboarding.css';

const TOTAL_STEPS = 4;
const SAMPLE_TEAMS = [
  { name: 'Accounts', description: 'Billing, invoices, collections and payments' },
  { name: 'Sales', description: 'Leads, deals and customer acquisition' },
  { name: 'Operations', description: 'Day-to-day execution and coordination' },
  { name: 'Support', description: 'Customer support and issue resolution' },
  { name: 'HR', description: 'People operations and attendance management' },
];
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  process.env.REACT_APP_GOOGLE_MAP_KEY ||
  '';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const COMPANY_MARKER_ICON = L.divIcon({
  className: 'company-location-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #111;
      border: 3px solid #fff;
      box-shadow: 0 8px 18px rgba(0,0,0,0.28), 0 0 0 4px rgba(17,17,17,0.14);
      position: relative;
    ">
      <div style="
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #fff;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      "></div>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

const OnboardingMapClickHandler = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }),
  });
  return null;
};

const Onboarding = ({ embedded = false, onClose = null, onCompleted = null }) => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [resolvedCompanyId, setResolvedCompanyId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Step 1: Company Profile
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyCoordinates, setCompanyCoordinates] = useState(null);
  const [companyGstin, setCompanyGstin] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [showAddressMap, setShowAddressMap] = useState(false);
  const initialAddressRef = useRef('');
  const initialCoordsRef = useRef(null);
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showPlaceSuggestions, setShowPlaceSuggestions] = useState(false);
  const [isPlaceSearching, setIsPlaceSearching] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);
  const suggestionsRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const {
    isLoadingLocation,
    locationError,
    getCurrentLocationWithAddress,
    setLocationError,
  } = useLocationHook();

  // Step 2: Teams
  const [teams, setTeams] = useState([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDesc, setNewTeamDesc] = useState('');
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeamDesc, setEditTeamDesc] = useState('');
  const [teamUpdatingId, setTeamUpdatingId] = useState(null);

  // Step 3: Work Timings
  const [workingDays, setWorkingDays] = useState(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  const [workStartTime, setWorkStartTime] = useState('09:00');
  const [workEndTime, setWorkEndTime] = useState('17:00');
  const [lunchStart, setLunchStart] = useState('13:00');
  const [lunchEnd, setLunchEnd] = useState('14:00');
  const [graceMinutes, setGraceMinutes] = useState(15);

  // Step 4: Features
  const [featureList, setFeatureList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [featureMatrix, setFeatureMatrix] = useState({});

  useEffect(() => {
    if (company?.id) {
      setResolvedCompanyId(company.id);
      return;
    }
    try {
      const storedCompany = JSON.parse(localStorage.getItem('company') || '{}');
      if (storedCompany?.id) {
        setResolvedCompanyId(storedCompany.id);
      }
    } catch (_) {
      // Ignore parse issues and rely on auth context
    }
  }, [company?.id]);

  useEffect(() => {
    if (company || resolvedCompanyId) {
      setCompanyName(company.name || '');
      setCompanyPhone(company.phone || '');
      setCompanyWebsite(company.website || '');
      setLogoPreview(company.logo || '');
      loadCompanyDetails();
    }
  }, [company?.id, resolvedCompanyId]);

  const loadCompanyDetails = async () => {
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) return;
    try {
      const { data } = await api.get(`/api/companies/${companyId}`);
      if (data?.is_success && data.company) {
        const c = data.company;
        const loadedAddress = c.address?.address || (typeof c.address === 'string' ? c.address : '');
        const loadedCoords = c.address?.coords || null;
        setCompanyName(c.name || '');
        setCompanyPhone(c.phone || '');
        setCompanyWebsite(c.website || '');
        setCompanyAddress(loadedAddress);
        setCompanyCoordinates(loadedCoords);
        setCompanyGstin(c.gstin || '');
        if (c.logo) setLogoPreview(c.logo);
        initialAddressRef.current = loadedAddress || '';
        initialCoordsRef.current = loadedCoords || null;
        setShowAddressMap(false);
      }
    } catch (e) {
      console.error('Failed to load company details:', e);
    }
  };

  const loadTeams = async () => {
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) return;
    try {
      const { data } = await api.get(`/api/departments/${companyId}`);
      if (data?.is_success) {
        setTeams(data.departments || []);
      }
    } catch (e) {
      console.error('Failed to load teams:', e);
    }
  };

  const loadFeatureAccess = async () => {
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) return;
    try {
      const resp = await FeatureAccessAPI.getPolicies(companyId);
      if (resp?.is_success) {
        setFeatureList(resp.features || []);
        setDepartments(resp.departments || []);
        const enabledMatrix = {};
        for (const p of (resp.policies || [])) {
          if (p.allow) {
            enabledMatrix[`${p.departmentId}:${p.featureKey}`] = true;
          }
        }
        setFeatureMatrix(enabledMatrix);
      }
    } catch (e) {
      console.error('Failed to load features:', e);
    }
  };

  useEffect(() => {
    if (currentStep === 2) loadTeams();
    if (currentStep === 4) loadFeatureAccess();
  }, [currentStep]);

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  // Step 1: Save company profile + logo
  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo must be under 5MB.');
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    const companyId = resolvedCompanyId || company?.id;
    if (!logoFile || !companyId) return null;
    setUploadingLogo(true);
    try {
      const { data: presigned } = await api.get('/api/upload/presigned-url', {
        params: { fileType: logoFile.type, dir: 'logos' },
      });
      const uploadUrl = presigned?.uploadUrl || presigned?.url;
      const fileUrl = presigned?.fileUrl || (uploadUrl ? String(uploadUrl).split('?')[0] : null);

      if (!uploadUrl) {
        throw new Error('Presigned upload URL missing in response');
      }

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': logoFile.type },
        body: logoFile,
      });
      return fileUrl;
    } catch (e) {
      console.error('Logo upload failed:', e);
      throw e;
    } finally {
      setUploadingLogo(false);
    }
  };

  const saveCompanyProfile = async () => {
    clearMessages();
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) {
      setError('Company context not found. Please sign in again.');
      return false;
    }
    if (!companyAddress.trim()) {
      setError('Company address is required.');
      return false;
    }
    setSaving(true);
    try {
      let logoUrl = null;
      if (logoFile) {
        logoUrl = await uploadLogo();
      }

      const payload = {
        name: companyName.trim(),
        phone: companyPhone.trim(),
        website: companyWebsite.trim() || undefined,
        address: companyAddress.trim()
          ? {
              address: companyAddress.trim(),
              coords: companyCoordinates || undefined,
            }
          : undefined,
        gstin: companyGstin.trim() || undefined,
      };
      if (logoUrl) payload.logo = logoUrl;

      await api.put(`/api/companies/${companyId}`, payload);
      setSuccess('Company profile saved!');
      setLogoFile(null);
      return true;
    } catch (e) {
      setError('Failed to save company profile.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Step 2: Add team
  const handleAddTeam = async () => {
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) {
      setError('Company context not found. Please sign in again.');
      return;
    }
    if (!newTeamName.trim()) {
      setError('Team name is required.');
      return;
    }
    clearMessages();
    setSaving(true);
    try {
      const { data } = await api.post('/api/departments/create', {
        name: newTeamName.trim(),
        description: newTeamDesc.trim() || undefined,
        companyId,
      });
      if (data?.is_success) {
        setNewTeamName('');
        setNewTeamDesc('');
        setSuccess('Team created!');
        await loadTeams();
      } else {
        setError(data?.message || 'Failed to create team.');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create team.');
    } finally {
      setSaving(false);
    }
  };

  const startEditTeam = (team) => {
    setEditingTeamId(team.id);
    setEditTeamName(team.name || '');
    setEditTeamDesc(team.description || '');
    clearMessages();
  };

  const cancelEditTeam = () => {
    setEditingTeamId(null);
    setEditTeamName('');
    setEditTeamDesc('');
  };

  const saveEditedTeam = async (teamId) => {
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) {
      setError('Company context not found. Please sign in again.');
      return;
    }
    if (!editTeamName.trim()) {
      setError('Team name is required.');
      return;
    }
    clearMessages();
    setTeamUpdatingId(teamId);
    try {
      const { data } = await api.put(`/api/departments/update/${teamId}`, {
        name: editTeamName.trim(),
        description: editTeamDesc.trim() || undefined,
        companyId,
      });
      if (data?.is_success) {
        setSuccess('Team updated.');
        cancelEditTeam();
        await loadTeams();
      } else {
        setError(data?.message || 'Failed to update team.');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to update team.');
    } finally {
      setTeamUpdatingId(null);
    }
  };

  const handleAddSampleTeam = async (team) => {
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) {
      setError('Company context not found. Please sign in again.');
      return;
    }
    const alreadyExists = teams.some(
      (t) => String(t.name || '').trim().toLowerCase() === team.name.toLowerCase()
    );
    if (alreadyExists) {
      setSuccess(`${team.name} team already exists.`);
      return;
    }
    clearMessages();
    setSaving(true);
    try {
      const { data } = await api.post('/api/departments/create', {
        name: team.name,
        description: team.description,
        companyId,
      });
      if (data?.is_success) {
        setSuccess(`${team.name} team created.`);
        await loadTeams();
      } else {
        setError(data?.message || `Failed to create ${team.name} team.`);
      }
    } catch (e) {
      setError(e.response?.data?.message || `Failed to create ${team.name} team.`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddDefaultTeams = async () => {
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) {
      setError('Company context not found. Please sign in again.');
      return;
    }
    clearMessages();
    setSaving(true);
    try {
      const existingNames = new Set(
        teams.map((t) => String(t.name || '').trim().toLowerCase())
      );
      const toCreate = SAMPLE_TEAMS.filter(
        (team) => !existingNames.has(team.name.toLowerCase())
      );
      if (toCreate.length === 0) {
        setSuccess('All sample teams already exist.');
        return;
      }
      for (const team of toCreate) {
        await api.post('/api/departments/create', {
          name: team.name,
          description: team.description,
          companyId,
        });
      }
      setSuccess(`Added ${toCreate.length} sample teams.`);
      await loadTeams();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to add sample teams.');
    } finally {
      setSaving(false);
    }
  };

  // Step 3: Save work timings
  const saveWorkTimings = async () => {
    clearMessages();
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) {
      setError('Company context not found. Please sign in again.');
      return false;
    }
    setSaving(true);
    try {
      await api.put(`/api/companies/${companyId}/attendance-settings`, {
        workingDays,
        workStartTime: workStartTime + ':00',
        workEndTime: workEndTime + ':00',
        lunchBreakStart: lunchStart + ':00',
        lunchBreakEnd: lunchEnd + ':00',
        graceTimeInMinutes: graceMinutes,
        isActive: true,
      });
      setSuccess('Work timings saved!');
      return true;
    } catch (e) {
      setError('Failed to save work timings.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Step 4: Team-wise feature assignment
  const toggleFeatureForTeam = (departmentId, featureKey) => {
    setFeatureMatrix((prev) => {
      const next = { ...prev };
      const key = `${departmentId}:${featureKey}`;
      const currentlyEnabled = !!next[key];
      const willEnable = !currentlyEnabled;

      if (willEnable) {
        next[key] = true;
        if (['ORDERS', 'POS', 'ACCOUNTS'].includes(featureKey)) {
          next[`${departmentId}:INVENTORY`] = true;
        }
      } else {
        if (featureKey === 'INVENTORY') {
          const hasDependentEnabled = ['ORDERS', 'POS', 'ACCOUNTS'].some(
            (depKey) => !!next[`${departmentId}:${depKey}`]
          );
          if (hasDependentEnabled) return next;
        }
        delete next[key];
      }

      return next;
    });
  };

  const saveFeatures = async () => {
    clearMessages();
    const companyId = resolvedCompanyId || company?.id;
    if (!companyId) {
      setError('Company context not found. Please sign in again.');
      return false;
    }
    setSaving(true);
    try {
      if (departments.length === 0) {
        setSuccess('No teams to configure features for. Skipping.');
        return true;
      }

      const policies = [];
      for (const dept of departments) {
        for (const feat of featureList) {
          if (featureMatrix[`${dept.id}:${feat.key}`]) {
            policies.push({ departmentId: dept.id, featureKey: feat.key, allow: true });
          }
        }
      }
      const resp = await FeatureAccessAPI.putPolicies(companyId, policies);
      if (resp?.is_success) {
        setSuccess('Features configured!');
        return true;
      }
      setError('Failed to save features.');
      return false;
    } catch (e) {
      setError('Failed to save features.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    let canProceed = true;
    if (currentStep === 1) canProceed = await saveCompanyProfile();
    else if (currentStep === 3) canProceed = await saveWorkTimings();
    else if (currentStep === 4) canProceed = await saveFeatures();

    if (canProceed) {
      if (currentStep >= TOTAL_STEPS) {
        if (onCompleted) onCompleted();
        else navigate('/dashboard');
      } else {
        clearMessages();
        setCurrentStep((s) => s + 1);
      }
    }
  };

  const handleSkip = () => {
    clearMessages();
    if (currentStep === 1 && !companyAddress.trim()) {
      setError('Company address is required.');
      return;
    }
    if (currentStep >= TOTAL_STEPS) {
      if (onCompleted) onCompleted();
      else navigate('/dashboard');
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    clearMessages();
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const dayLabels = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' },
  ];

  const featureDescriptions = {
    ACCOUNTS: 'Quotes, Proformas, Invoices -- create, edit, send, PDFs.',
    ORDERS: 'Order templates and customer orders -- create, track, fulfill.',
    POS: 'Point of Sale -- quick billing for walk-in sales.',
    INVENTORY: 'Items & services catalog, pricing, taxes.',
    EXPENSES: 'Expense tracking, approvals, reimbursements, OCR receipt scanning.',
    ATTENDANCE: 'Employee attendance tracking, check-in/out, calendar view.',
    LOCATION: 'Real-time employee location tracking on map.',
  };

  const stepMeta = [
    { num: 1, label: 'Company Profile', icon: <FiBriefcase /> },
    { num: 2, label: 'Teams', icon: <FiUsers /> },
    { num: 3, label: 'Work Timings', icon: <FiClock /> },
    { num: 4, label: 'Features', icon: <FiGrid /> },
  ];

  const isAddressChanged = (nextAddress, nextCoordinates) => {
    const addrChanged =
      String(nextAddress || '').trim() !== String(initialAddressRef.current || '').trim();
    const base = initialCoordsRef.current;
    const coordChanged = !!nextCoordinates && (
      !base ||
      Number(nextCoordinates.lat) !== Number(base.lat) ||
      Number(nextCoordinates.lng) !== Number(base.lng)
    );
    return addrChanged || coordChanged;
  };

  useEffect(() => {
    let cancelled = false;
    const setupGooglePlaces = async () => {
      if (!GOOGLE_MAPS_API_KEY) return;
      const existing = document.getElementById('google-maps-places-script');
      if (!existing) {
        const script = document.createElement('script');
        script.id = 'google-maps-places-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
        });
      } else if (!(window.google && window.google.maps && window.google.maps.places)) {
        await new Promise((resolve) => {
          const wait = () => {
            if (window.google && window.google.maps && window.google.maps.places) return resolve();
            setTimeout(wait, 60);
          };
          wait();
        });
      }

      if (!cancelled && window.google?.maps?.places) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
        geocoderRef.current = new window.google.maps.Geocoder();
        setMapsReady(true);
      }
    };

    setupGooglePlaces().catch(() => {
      if (!cancelled) setLocationError('Google Places failed to load. Please try again.');
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowPlaceSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddressInputChange = (value) => {
    setCompanyAddress(value);
    setShowAddressMap(isAddressChanged(value, companyCoordinates));
    if (value && locationError) setLocationError('');
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!value || value.length < 3) {
      setPlaceSuggestions([]);
      setShowPlaceSuggestions(false);
      return;
    }
    if (!mapsReady || !autocompleteServiceRef.current) {
      setLocationError('Google Maps search is not ready yet.');
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      setIsPlaceSearching(true);
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: 'in' },
          types: ['establishment'],
        },
        (predictions, status) => {
          const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK;
          if (status === OK && Array.isArray(predictions) && predictions.length > 0) {
            setPlaceSuggestions(predictions);
            setShowPlaceSuggestions(true);
          } else {
            autocompleteServiceRef.current.getPlacePredictions(
              {
                input: value,
                componentRestrictions: { country: 'in' },
              },
              (fallbackPredictions, fallbackStatus) => {
                if (fallbackStatus === OK && Array.isArray(fallbackPredictions)) {
                  setPlaceSuggestions(fallbackPredictions);
                  setShowPlaceSuggestions(fallbackPredictions.length > 0);
                } else {
                  setPlaceSuggestions([]);
                  setShowPlaceSuggestions(false);
                }
              }
            );
          }
          setIsPlaceSearching(false);
        }
      );
    }, 300);
  };

  const handleAddressSuggestion = (suggestion) => {
    if (!placesServiceRef.current || !suggestion?.place_id) return;
    setIsPlaceSearching(true);
    placesServiceRef.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: [
          'name',
          'formatted_address',
          'geometry',
          'website',
          'formatted_phone_number',
          'international_phone_number',
        ],
      },
      (place, status) => {
        const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK;
        if (status !== OK || !place) {
          setIsPlaceSearching(false);
          return;
        }
        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();
        const nextAddress = place.formatted_address || suggestion.description || '';
        const nextCoordinates = (lat != null && lng != null) ? { lat, lng } : null;
        setCompanyAddress(nextAddress);
        setCompanyCoordinates(nextCoordinates);
        setShowAddressMap(isAddressChanged(nextAddress, nextCoordinates));
        if (!companyName && place.name) setCompanyName(place.name);
        if (!companyWebsite && place.website) setCompanyWebsite(place.website);
        if (!companyPhone && (place.formatted_phone_number || place.international_phone_number)) {
          const phone = (place.formatted_phone_number || place.international_phone_number || '').replace(/\D/g, '').slice(0, 15);
          setCompanyPhone(phone);
        }
        setShowPlaceSuggestions(false);
        setPlaceSuggestions([]);
        setMapKey((prev) => prev + 1);
        setIsPlaceSearching(false);
      }
    );
  };

  const handleGetCurrentLocation = async () => {
    const result = await getCurrentLocationWithAddress();
    if (result.success) {
      const nextAddress = result.location.address || '';
      const nextCoordinates = result.location.coordinates || null;
      setCompanyAddress(nextAddress);
      setCompanyCoordinates(nextCoordinates);
      setShowAddressMap(isAddressChanged(nextAddress, nextCoordinates));
      setMapKey((prev) => prev + 1);
      if (result.warning) setLocationError(result.warning);
      return;
    }
    setLocationError(result.error || 'Unable to get current location.');
  };

  const handleMapPick = async ({ lat, lng }) => {
    setCompanyCoordinates({ lat, lng });
    setShowAddressMap(true);
    if (geocoderRef.current) {
      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        const OK = window.google?.maps?.GeocoderStatus?.OK;
        if (status === OK && results?.[0]?.formatted_address) {
          const nextAddress = results[0].formatted_address;
          setCompanyAddress(nextAddress);
          setShowAddressMap(isAddressChanged(nextAddress, { lat, lng }));
        }
      });
    }
  };

  const renderStep1 = () => (
    <div className="onboarding-step-content">
      <h2>Company Profile</h2>
      <p className="onboarding-step-desc">
        Add your logo and details. These appear on invoices, quotes, and PDFs.
      </p>

      <div className="onboarding-logo-upload">
        <div
          className="onboarding-logo-preview"
          onClick={() => document.getElementById('logo-input').click()}
        >
          {logoPreview ? (
            <img src={logoPreview} alt="Company logo" />
          ) : (
            <div className="onboarding-logo-placeholder">
              <FiImage size={32} />
              <span>Upload Logo</span>
            </div>
          )}
          <div className="onboarding-logo-overlay">
            <FiUpload size={20} />
          </div>
        </div>
        <input
          id="logo-input"
          type="file"
          accept="image/*"
          onChange={handleLogoSelect}
          hidden
        />
        {uploadingLogo && <span className="onboarding-uploading"><FiLoader className="spin" /> Uploading...</span>}
      </div>

      <div className="onboarding-form">

        <div className="onboarding-field">
          <label>Company Name</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Company name"
          />
        </div>
        <div className="onboarding-row">
          <div className="onboarding-field">
            <label>Phone</label>
            <input
              type="tel"
              value={companyPhone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '');
                if (val.length <= 15) setCompanyPhone(val);
              }}
              placeholder="Phone number"
            />
          </div>
          <div className="onboarding-field">
            <label>Website</label>
            <input
              type="url"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>
        <div className="onboarding-field">
          <label>GSTIN <span className="optional">(optional)</span></label>
          <input
            type="text"
            value={companyGstin}
            onChange={(e) => {
              if (e.target.value.length <= 15) setCompanyGstin(e.target.value.toUpperCase());
            }}
            placeholder="e.g. 29ABCDE1234F1Z5"
            maxLength={15}
          />
        </div>
        <div className="onboarding-field">
          <label>Address *</label>
          <div className="onboarding-location-input-wrap" ref={suggestionsRef}>
            <input
              type="text"
              value={companyAddress}
              onChange={(e) => handleAddressInputChange(e.target.value)}
              placeholder="Search business address in India"
              required
            />
            {showPlaceSuggestions && placeSuggestions.length > 0 && (
              <div className="onboarding-location-suggestions">
                {placeSuggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.place_id || index}`}
                    type="button"
                    className="onboarding-location-suggestion-item"
                    onClick={() => handleAddressSuggestion(suggestion)}
                  >
                    {suggestion.description}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className="onboarding-location-btn"
            type="button"
            onClick={handleGetCurrentLocation}
            disabled={isLoadingLocation || isPlaceSearching}
          >
            {isLoadingLocation ? <FiLoader className="spin" /> : <FiMapPin />} Use current location
          </button>
          {isPlaceSearching && <div className="onboarding-location-hint">Searching places...</div>}
          {locationError && <div className="onboarding-location-error">{locationError}</div>}
          {showAddressMap && (
            <div className="onboarding-map-wrap">
              {companyCoordinates?.lat && companyCoordinates?.lng ? (
                <MapContainer
                  key={mapKey}
                  center={[companyCoordinates.lat, companyCoordinates.lng]}
                  zoom={15}
                  scrollWheelZoom={false}
                  style={{ height: 220, width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <OnboardingMapClickHandler onPick={handleMapPick} />
                  <Marker
                    position={[companyCoordinates.lat, companyCoordinates.lng]}
                    icon={COMPANY_MARKER_ICON}
                    draggable
                    eventHandlers={{
                      dragend: (e) => {
                        const pos = e.target.getLatLng();
                        handleMapPick({ lat: pos.lat, lng: pos.lng });
                      }
                    }}
                  >
                    <Popup>Company location</Popup>
                  </Marker>
                </MapContainer>
              ) : (
                <MapContainer
                  center={[20.5937, 78.9629]}
                  zoom={4}
                  scrollWheelZoom={false}
                  style={{ height: 220, width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <OnboardingMapClickHandler onPick={handleMapPick} />
                </MapContainer>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="onboarding-step-content">
      <h2>Create Teams</h2>
      <p className="onboarding-step-desc">
        Organize your employees into departments or teams. You can always add more later.
      </p>

      <div className="onboarding-sample-teams">
        <div className="onboarding-sample-teams-header">
          <span>Quick add teams</span>
          <button
            type="button"
            className="onboarding-add-samples-btn"
            onClick={handleAddDefaultTeams}
            disabled={saving}
          >
            Add default teams
          </button>
        </div>
        <div className="onboarding-sample-team-chips">
          {SAMPLE_TEAMS.map((team) => (
            <button
              key={team.name}
              type="button"
              className="onboarding-sample-team-chip"
              onClick={() => handleAddSampleTeam(team)}
              disabled={saving}
              title={team.description}
            >
              + {team.name}
            </button>
          ))}
        </div>
      </div>

      {teams.length > 0 && (
        <div className="onboarding-teams-list">
          {teams.map((t) => (
            <div key={t.id} className="onboarding-team-item">
              <div className="onboarding-team-icon"><FiUsers /></div>
              <div className="onboarding-team-content">
                {editingTeamId === t.id ? (
                  <div className="onboarding-team-edit-form">
                    <input
                      type="text"
                      value={editTeamName}
                      onChange={(e) => setEditTeamName(e.target.value)}
                      placeholder="Team name"
                    />
                    <input
                      type="text"
                      value={editTeamDesc}
                      onChange={(e) => setEditTeamDesc(e.target.value)}
                      placeholder="Description (optional)"
                    />
                    <div className="onboarding-team-actions">
                      <button
                        type="button"
                        className="onboarding-team-action-btn primary"
                        onClick={() => saveEditedTeam(t.id)}
                        disabled={teamUpdatingId === t.id || !editTeamName.trim()}
                      >
                        {teamUpdatingId === t.id ? <FiLoader className="spin" /> : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="onboarding-team-action-btn"
                        onClick={cancelEditTeam}
                        disabled={teamUpdatingId === t.id}
                      >
                        <FiX /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="onboarding-team-header">
                      <div className="onboarding-team-name">{t.name}</div>
                      <button
                        type="button"
                        className="onboarding-team-action-link"
                        onClick={() => startEditTeam(t)}
                      >
                        <FiEdit2 /> Edit
                      </button>
                    </div>
                    {t.description && <div className="onboarding-team-desc">{t.description}</div>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="onboarding-add-team">
        <div className="onboarding-field">
          <label>Team Name *</label>
          <input
            type="text"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            placeholder="e.g. Sales, Operations, Delivery"
            onKeyDown={(e) => e.key === 'Enter' && handleAddTeam()}
          />
        </div>
        <div className="onboarding-field">
          <label>Description <span className="optional">(optional)</span></label>
          <input
            type="text"
            value={newTeamDesc}
            onChange={(e) => setNewTeamDesc(e.target.value)}
            placeholder="Brief description"
          />
        </div>
        <button
          className="onboarding-add-btn"
          onClick={handleAddTeam}
          disabled={saving || !newTeamName.trim()}
          type="button"
        >
          {saving ? <FiLoader className="spin" /> : <FiPlus />} Add Team
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="onboarding-step-content">
      <h2>Work Timings</h2>
      <p className="onboarding-step-desc">
        Set default working hours and days for attendance tracking.
      </p>

      <div className="onboarding-form">
        <div className="onboarding-field">
          <label>Working Days</label>
          <div className="onboarding-day-pills">
            {dayLabels.map((d) => (
              <button
                key={d.key}
                type="button"
                className={`onboarding-day-pill ${workingDays.includes(d.key) ? 'active' : ''}`}
                onClick={() => {
                  setWorkingDays((prev) =>
                    prev.includes(d.key)
                      ? prev.filter((x) => x !== d.key)
                      : [...prev, d.key]
                  );
                }}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="onboarding-row">
          <div className="onboarding-field">
            <label>Work Start</label>
            <input type="time" value={workStartTime} onChange={(e) => setWorkStartTime(e.target.value)} />
          </div>
          <div className="onboarding-field">
            <label>Work End</label>
            <input type="time" value={workEndTime} onChange={(e) => setWorkEndTime(e.target.value)} />
          </div>
        </div>

        <div className="onboarding-row">
          <div className="onboarding-field">
            <label>Lunch Start</label>
            <input type="time" value={lunchStart} onChange={(e) => setLunchStart(e.target.value)} />
          </div>
          <div className="onboarding-field">
            <label>Lunch End</label>
            <input type="time" value={lunchEnd} onChange={(e) => setLunchEnd(e.target.value)} />
          </div>
        </div>

        <div className="onboarding-field">
          <label>Grace Period (minutes)</label>
          <input
            type="number"
            min="0"
            max="60"
            value={graceMinutes}
            onChange={(e) => setGraceMinutes(parseInt(e.target.value, 10) || 0)}
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="onboarding-step-content">
      <h2>Enable Features</h2>
      <p className="onboarding-step-desc">
        Assign features team-wise. You can change these anytime in Settings.
      </p>

      {featureList.length === 0 || departments.length === 0 ? (
        <p className="onboarding-muted">
          Create at least one team first to configure feature assignment.
        </p>
      ) : (
        <div className="onboarding-feature-matrix-wrap">
          <table className="onboarding-feature-matrix">
            <thead>
              <tr>
                <th>Team</th>
                {featureList.map((f) => (
                  <th key={f.key} title={featureDescriptions[f.key] || f.label}>
                    {f.label || f.key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {departments.map((dept) => (
                <tr key={dept.id}>
                  <td className="onboarding-feature-team-name">{dept.name}</td>
                  {featureList.map((feature) => {
                    const matrixKey = `${dept.id}:${feature.key}`;
                    const checked = !!featureMatrix[matrixKey];
                    const isInventoryForced =
                      feature.key === 'INVENTORY' &&
                      ['ORDERS', 'POS', 'ACCOUNTS'].some(
                        (depKey) => !!featureMatrix[`${dept.id}:${depKey}`]
                      );
                    return (
                      <td key={matrixKey}>
                        <input
                          type="checkbox"
                          checked={checked || isInventoryForced}
                          disabled={isInventoryForced}
                          onChange={() => toggleFeatureForTeam(dept.id, feature.key)}
                          title={featureDescriptions[feature.key] || feature.label}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const stepRenderers = [renderStep1, renderStep2, renderStep3, renderStep4];

  return (
    <div className={`onboarding-container ${embedded ? 'onboarding-container--embedded' : ''}`}>
      <div className={`onboarding-card ${embedded ? 'onboarding-card--embedded' : ''}`}>
        {embedded && onClose && (
          <button
            type="button"
            className="onboarding-modal-close"
            onClick={onClose}
            aria-label="Close quick setup"
          >
            <FiX />
          </button>
        )}
        <div className="onboarding-welcome-header">
          <h1>Welcome to BloomNav</h1>
          <p>Lets get you started</p>
        </div>

        {/* Progress bar */}
        <div className="onboarding-progress">
          {stepMeta.map((s) => (
            <div
              key={s.num}
              className={`onboarding-progress-step ${currentStep === s.num ? 'current' : ''} ${currentStep > s.num ? 'done' : ''}`}
            >
              <div className="onboarding-progress-circle">
                {currentStep > s.num ? <FiCheck /> : s.icon}
              </div>
              <span className="onboarding-progress-label">{s.label}</span>
            </div>
          ))}
          <div className="onboarding-progress-bar">
            <div
              className="onboarding-progress-fill"
              style={{ width: `${((currentStep - 1) / (TOTAL_STEPS - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="onboarding-error" role="alert">
            {error}
            <button type="button" onClick={() => setError('')}>&times;</button>
          </div>
        )}
        {success && (
          <div className="onboarding-success" role="status">
            {success}
          </div>
        )}

        {/* Step content */}
        {stepRenderers[currentStep - 1]?.()}

        {/* Navigation */}
        <div className="onboarding-nav">
          <div className="onboarding-nav-left">
            {currentStep > 1 && (
              <button className="onboarding-nav-btn secondary" onClick={handleBack} type="button">
                <FiArrowLeft /> Back
              </button>
            )}
          </div>
          <div className="onboarding-nav-right">
            <button className="onboarding-nav-btn skip" onClick={handleSkip} type="button">
              {currentStep >= TOTAL_STEPS ? 'Skip & Finish' : 'Skip'}
            </button>
            <button
              className="onboarding-nav-btn primary"
              onClick={handleNext}
              disabled={saving}
              type="button"
            >
              {saving ? (
                <><FiLoader className="spin" /> Saving...</>
              ) : currentStep >= TOTAL_STEPS ? (
                <><FiCheck /> Finish Setup</>
              ) : (
                'Save & Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
